import React from "react";
import LegalPageLayout, { Section, Callout, Plain } from "./legal/LegalPageLayout";

const SECTIONS = [
  { id: "overview", number: 1, title: "Overview" },
  { id: "information-we-collect", number: 2, title: "Information we collect" },
  { id: "chat-retention", number: 3, title: "Chat content, deletion & staff review" },
  { id: "school-consent", number: 4, title: "Schools, parents & consent" },
  { id: "how-we-use-information", number: 5, title: "How we use information" },
  { id: "who-can-access", number: 6, title: "Who can access student data" },
  { id: "data-security", number: 7, title: "Data security" },
  { id: "childrens-privacy", number: 8, title: "Children's privacy (COPPA)" },
  { id: "your-choices", number: 9, title: "Your choices" },
  { id: "changes", number: 10, title: "Changes to this policy" },
  { id: "contact", number: 11, title: "Contact us" },
];

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="August 13, 2026"
      intro="LearnAndMasterAI is an AI tutoring platform that schools provide to their students and staff. This policy explains what information we collect through the product, how it's used, and — because this is a K-12 safety product, not a consumer chat app — how chat content is retained and reviewed even after a student deletes it."
      sections={SECTIONS}
      crossLinkTo="/terms"
      crossLinkLabel="Terms of Service"
    >
      <Section id="overview" number={1} title="Overview">
        <p>
          This policy covers the LearnAndMasterAI web application: the chat-based tutor, class and
          curriculum tools, and the admin dashboards schools use to manage them. It applies to
          students, teachers, and school administrators who use an account provisioned by a school.
        </p>
        <p>
          We don't sell personal information, and we don't offer public self-signup — every account
          on this platform is created by a school, which is why several sections below reference the
          school's role as the account holder rather than the individual student's.
        </p>
      </Section>

      <Section id="information-we-collect" number={2} title="Information we collect">
        <p>We collect the information needed to run the product and to keep students safe while using it:</p>
        <ul>
          <li>
            <strong>Account information</strong> — name, username, email, school and role (student,
            teacher, or administrator), and class or section assignments provided by your school.
          </li>
          <li>
            <strong>Chat messages</strong> — the content of conversations with the AI tutor, including
            any text a student types and any responses generated.
          </li>
          <li>
            <strong>Uploaded files</strong> — documents, images, or assignments a student attaches to a
            conversation for the tutor to reference.
          </li>
          <li>
            <strong>Safety flags</strong> — automated and staff-reviewed annotations on messages that
            our safety detection identifies as potentially concerning (for example, references to
            self-harm, harassment, or other risk indicators).
          </li>
          <li>
            <strong>Usage information</strong> — timestamps, session activity, and basic device/browser
            information needed for security and troubleshooting.
          </li>
        </ul>
      </Section>

      <Section id="chat-retention" number={3} title="Chat content, deletion & staff review">
        <Callout>
          Deleting a chat removes it from a student's own history view, but the underlying content is
          retained and remains reviewable by authorized school staff. This exists specifically so
          flagged or concerning conversations can't be erased before a school can respond to a
          safety concern.
        </Callout>
        <p>
          When a student deletes a conversation, we perform what's technically called a{" "}
          <strong>soft delete</strong>: the chat disappears from that student's own chat list, but the
          messages are not immediately or permanently erased from our systems. This is a deliberate
          safety design choice, not a bug or an oversight — it ensures a student can't delete evidence
          of a safety concern (their own or, in a shared conversation, someone else's) before school
          staff have had a chance to review it.
        </p>
        <p>
          Retained chat content, including deleted chats, is accessible only to school staff with an
          administrative or safety-review role at the student's school — see{" "}
          <strong>Section 6</strong> for exactly who that is. It is not visible to other students, and
          we do not use it for advertising or share it with third parties for marketing purposes.
        </p>
      </Section>

      <Section id="school-consent" number={4} title="Schools, parents & consent">
        <p>
          LearnAndMasterAI is provided to students through their school, under an agreement between
          us and the school. In this model, the school — not LearnAndMasterAI directly — is
          responsible for obtaining any parental consent required under FERPA, COPPA, or applicable
          state student-data-privacy law before creating a student account, and for notifying parents
          about the platform as part of its normal enrollment and technology-use disclosures.
        </p>
        <p>
          We treat chat and account data created through the product as an{" "}
          <strong>education record</strong> under the school's control. Schools can direct us to
          provide, correct, or delete a student's data consistent with their own FERPA obligations —
          see <strong>Section 8</strong> for how this works for children under 13 specifically.
        </p>
      </Section>

      <Section id="how-we-use-information" number={5} title="How we use information">
        <p>We use the information described in Section 2 to:</p>
        <ul>
          <li>Provide the tutoring chat, curriculum tools, and dashboards themselves.</li>
          <li>Detect and route safety flags to the right school staff.</li>
          <li>Maintain an audit trail school staff can use to review flagged conversations.</li>
          <li>Secure accounts, prevent abuse, and troubleshoot technical issues.</li>
          <li>Improve the accuracy and reliability of the tutoring experience.</li>
        </ul>
      </Section>

      <Section id="who-can-access" number={6} title="Who can access student data">
        <Plain>
          Your teacher and your school's admin can both see safety-flagged chats for your account,
          including ones you deleted. Other students never can.
        </Plain>
        <p>Access to student chat content is limited by role:</p>
        <ul>
          <li>
            <strong>Students</strong> can see their own active (non-deleted) chat history.
          </li>
          <li>
            <strong>Teachers</strong> with classes assigned to them can see their own students' chat
            activity, and — under the same rule described in Section 3 — can see safety-flagged
            messages for those students even from a chat the student deleted.
          </li>
          <li>
            <strong>School administrators</strong> can review flagged conversations across their
            school, including safety-flagged messages from deleted chats.
          </li>
          <li>
            <strong>LearnAndMasterAI staff</strong> access data only as needed to operate, secure, and
            support the platform — for example, to investigate a technical issue a school reports.
          </li>
        </ul>
      </Section>

      <Section id="data-security" number={7} title="Data security">
        <p>
          We use industry-standard safeguards — encrypted connections, access controls scoped by role
          and school, and authenticated sessions — to protect account and chat data. No system is
          perfectly secure, and we ask school administrators to report suspected security issues to
          the contact below right away.
        </p>
      </Section>

      <Section id="childrens-privacy" number={8} title="Children's privacy (COPPA)">
        <p>
          Many LearnAndMasterAI users are under 13. Because accounts are created and managed by
          schools rather than by individual sign-up, we rely on the school's authority to consent on
          behalf of parents for the collection of student data needed to provide the service, as
          permitted under COPPA's "school official" consent model. Parents with questions about their
          child's account should contact their school first; schools can reach us at the contact below
          to request changes, exports, or deletion of a student's data.
        </p>
      </Section>

      <Section id="your-choices" number={9} title="Your choices">
        <p>
          Students can delete individual chats from their own history at any time (see{" "}
          <strong>Section 3</strong> for what that does and doesn't remove). For account-level
          changes — correcting information, exporting data, or requesting removal of a student's
          account — the request needs to come from or be authorized by the school, since the school
          is the account holder of record. Schools can reach us directly using the contact information
          below.
        </p>
      </Section>

      <Section id="changes" number={10} title="Changes to this policy">
        <p>
          We may update this policy as the product changes. If we make a material change to how we
          collect or use student data, we'll update the "Last updated" date above and notify schools
          so they can pass relevant changes along to families as needed.
        </p>
      </Section>

      <Section id="contact" number={11} title="Contact us">
        <p>
          Questions about this policy, or requests related to student data, can be sent to{" "}
          <strong>
            <a href="mailto:info@learnandmasterai.com">
              info@learnandmasterai.com
            </a>
          </strong>
          .
        </p>
      </Section>
    </LegalPageLayout>
  );
}
